import { Box3, Vector3, Matrix4 } from 'three';

/**
 * Berechnet die 2D-Bounding-Box eines 3D-Objekts aus der Kamera-Perspektive
 * Verwendet die exakte gleiche Projektionslogik wie Three.js beim Rendering
 */
export const calculateScreenBoundingBox = (object, camera, imageWidth, imageHeight) => {
    if (!object || !camera || !imageWidth || !imageHeight) {
        return null;
    }

    // Stelle sicher, dass alle Matrizen aktuell sind
    object.updateMatrixWorld(true);
    
    // WICHTIG: Stelle sicher, dass die Aspect-Ratio korrekt ist
    const correctAspect = imageWidth / imageHeight;
    const originalAspect = camera.aspect;
    const needsAspectUpdate = Math.abs(camera.aspect - correctAspect) > 0.001;
    
    if (camera.isPerspectiveCamera && needsAspectUpdate) {
        camera.aspect = correctAspect;
        camera.updateProjectionMatrix();
    }

    // Berechne Bounding Box im Welt-Raum
    const worldBox = new Box3();
    worldBox.setFromObject(object);
    
    if (worldBox.isEmpty()) {
        // Stelle Aspect-Ratio wieder her (nur wenn geändert)
        if (camera.isPerspectiveCamera && needsAspectUpdate) {
            camera.aspect = originalAspect;
            camera.updateProjectionMatrix();
        }
        return null;
    }

    // Hole alle 8 Ecken des Bounding Box
    const corners = [
        new Vector3(worldBox.min.x, worldBox.min.y, worldBox.min.z),
        new Vector3(worldBox.max.x, worldBox.min.y, worldBox.min.z),
        new Vector3(worldBox.min.x, worldBox.max.y, worldBox.min.z),
        new Vector3(worldBox.max.x, worldBox.max.y, worldBox.min.z),
        new Vector3(worldBox.min.x, worldBox.min.y, worldBox.max.z),
        new Vector3(worldBox.max.x, worldBox.min.y, worldBox.max.z),
        new Vector3(worldBox.min.x, worldBox.max.y, worldBox.max.z),
        new Vector3(worldBox.max.x, worldBox.max.y, worldBox.max.z),
    ];

    // Projiziere alle Ecken auf den Bildschirm
    const screenPoints = [];
    const viewMatrix = camera.matrixWorldInverse;
    const projectionMatrix = camera.projectionMatrix;
    
    for (const worldPoint of corners) {
        // Schritt 1: Transformiere zu Kamera-Koordinaten (View Space)
        const viewPoint = worldPoint.clone();
        viewPoint.applyMatrix4(viewMatrix);
        
        // Prüfe ob Punkt vor der Kamera liegt
        if (camera.isPerspectiveCamera) {
            // In View Space: z < 0 bedeutet vor der Kamera (Kamera schaut in -z Richtung)
            if (viewPoint.z >= -camera.near) {
                continue; // Punkt ist hinter der Near-Plane
            }
        }
        
        // Schritt 2: Projiziere mit der Projektionsmatrix
        const ndcPoint = viewPoint.clone();
        ndcPoint.applyMatrix4(projectionMatrix);
        
        // Perspektivische Division (w-Komponente)
        if (camera.isPerspectiveCamera && Math.abs(ndcPoint.w) > 0.0001) {
            ndcPoint.x /= ndcPoint.w;
            ndcPoint.y /= ndcPoint.w;
            ndcPoint.z /= ndcPoint.w;
        }
        
        // Schritt 3: Transformiere NDC zu Pixel-Koordinaten
        // NDC: x,y in [-1, 1], wobei y=1 oben ist
        // Pixel: x,y in [0, width/height], wobei y=0 oben ist
        const pixelX = ((ndcPoint.x + 1) / 2) * imageWidth;
        const pixelY = ((1 - ndcPoint.y) / 2) * imageHeight;
        
        // Nur gültige Punkte hinzufügen
        if (isFinite(pixelX) && isFinite(pixelY)) {
            screenPoints.push({ x: pixelX, y: pixelY });
        }
    }

    // Stelle Aspect-Ratio wieder her (nur wenn geändert)
    if (camera.isPerspectiveCamera && needsAspectUpdate) {
        camera.aspect = originalAspect;
        camera.updateProjectionMatrix();
    }

    if (screenPoints.length === 0) {
        return null;
    }

    // Berechne Bounding Box aus allen projizierten Punkten
    const xs = screenPoints.map(p => p.x);
    const ys = screenPoints.map(p => p.y);
    
    let minX = Math.min(...xs);
    let maxX = Math.max(...xs);
    let minY = Math.min(...ys);
    let maxY = Math.max(...ys);

    // Stelle sicher, dass die Box innerhalb des Bildes liegt
    minX = Math.max(0, Math.min(imageWidth, minX));
    maxX = Math.max(0, Math.min(imageWidth, maxX));
    minY = Math.max(0, Math.min(imageHeight, minY));
    maxY = Math.max(0, Math.min(imageHeight, maxY));

    const width = maxX - minX;
    const height = maxY - minY;

    // Nur zurückgeben wenn die Box eine sinnvolle Größe hat
    if (width < 1 || height < 1) {
        return null;
    }

    return {
        x: minX,
        y: minY,
        width: width,
        height: height
    };
};

/**
 * Berechnet Bounding Boxes für mehrere Objekte
 */
export const calculateMultipleBoundingBoxes = (objects, camera, imageWidth, imageHeight) => {
    if (!objects || !Array.isArray(objects) || objects.length === 0) {
        return [];
    }

    if (!camera || !imageWidth || !imageHeight) {
        return [];
    }

    const boundingBoxes = [];
    
    objects.forEach((object, index) => {
        if (!object) return;
        
        const bbox = calculateScreenBoundingBox(object, camera, imageWidth, imageHeight);
        if (bbox) {
            boundingBoxes.push({
                objectIndex: index,
                ...bbox
            });
        }
    });
    
    return boundingBoxes;
};
